import { RightPanelModule } from './RightPanelContext';

/**
 * Module Registry for RightPanel
 *
 * This registry allows modules to be registered at runtime from anywhere in the application.
 * It provides a centralized way to manage modules that can be displayed in the right panel.
 */

// Store for registered modules
const registeredModules: Map<string, RightPanelModule> = new Map();

// Event for module registration/unregistration
const moduleChangeEvent = 'rightPanelModuleChange';

// Custom event dispatcher
const dispatchModuleChangeEvent = () => {
  const event = new CustomEvent(moduleChangeEvent);
  window.dispatchEvent(event);
};

/**
 * Register a module with the registry
 * @param module The module to register
 */
export const registerModule = (module: RightPanelModule): void => {
  registeredModules.set(module.id, module);
  dispatchModuleChangeEvent();
};

/**
 * Unregister a module from the registry
 * @param moduleId The ID of the module to unregister
 */
export const unregisterModule = (moduleId: string): void => {
  registeredModules.delete(moduleId);
  dispatchModuleChangeEvent();
};

/**
 * Get all registered modules
 * @returns Array of registered modules
 */
export const getModules = (): RightPanelModule[] => {
  return Array.from(registeredModules.values());
};

/**
 * Get a specific module by ID
 * @param moduleId The ID of the module to retrieve
 * @returns The module or undefined if not found
 */
export const getModule = (moduleId: string): RightPanelModule | undefined => {
  return registeredModules.get(moduleId);
};

/**
 * Hook to subscribe to module registry changes
 * @param callback Function to call when modules change
 * @returns Cleanup function to remove event listener
 */
export const useModuleRegistry = (callback: () => void): (() => void) => {
  const handleModuleChange = () => {
    callback();
  };

  // Add event listener on mount, remove on unmount
  window.addEventListener(moduleChangeEvent, handleModuleChange);
  return () => {
    window.removeEventListener(moduleChangeEvent, handleModuleChange);
  };
};

// Export a default object for convenience
export default {
  registerModule,
  unregisterModule,
  getModules,
  getModule,
  useModuleRegistry,
};
